// Extruded 3D hex tiles per biome. Each hex is a tall bevelled prism whose
// top sits at the tile's own surface height (tileElevationAt), so
// neighbouring tiles step up and down and their side walls show. Lighting is
// baked into vertex colours (the material is unlit — see below), which is
// what gives the board its 3D read. Mountains get cone-like peaks and snow
// caps via onBeforeCompile.
//
// Uses instanced meshes -- one per biome group -- matching the existing pattern.
//
// No image textures: the /assets/textures/terrain/tile_*.png art is drawn as
// isometric tile illustrations, not top-down surfaces, and stretched over
// the hex tops it showed up as pale shapes offset from the hex grid.

import type { SceneNode } from '@aod/protocol';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { RenderLayer } from './RenderLayer.js';
import {
  CLOUD_SHADOW_GLSL_MAIN,
  CLOUD_SHADOW_GLSL_PARS,
  cloudShadowUniforms,
} from './cloudShadow.js';
import {
  FLAT_RELIEF,
  TILE_PLATEAU,
  TILE_RADIUS,
  TILE_RELIEF,
  hexNoise,
  smoothField,
  tileSurfaceY,
} from './elevation.js';
import { FROST_COLORS, type SeasonTint, applySeasonTint } from './seasons.js';
import {
  GLSL_UNPACK_RGB,
  buildTileEdgeAttributes,
  packRGB,
  setInstancedAttribute,
} from './terrainEdges.js';

interface TerrainTilesProps {
  readonly entries: Array<{ tile: SceneNode; idx: number }>;
  readonly biome: string;
  readonly count: number;
  readonly visibleCoords?: Set<string> | null;
  readonly exploredCoords?: Set<string>;
  readonly onPointerOver?: (tile: SceneNode | null) => void;
  readonly onClick?: (tile: SceneNode) => void;
  readonly biomeMap: Map<string, string>;
  /** "q,r" -> packed base colour of every tile (buildTileBaseColors), for edge blends. */
  readonly baseColors?: ReadonlyMap<string, number>;
  /** Per-tile flag texture (tileData.ts, P7.13): rivers, volcano/wall codes, levels. */
  readonly tileData?: TileData;
}

import type { TileData } from './tileData.js';
import {
  CRAG_AMP,
  FLAT_CRAG,
  glslFloat,
  makeTileGeometry,
  riverTime,
  tileEdgeShader,
} from './tileGeometry.js';
import { hexShape } from './tilePaint.js';
import { buildTileBaseColors, tileBaseColor, varyTileColor } from './tilePaint.js';
import { VOLCANO_SNOW_GLSL, VOLCANO_TINT_GLSL } from './volcanoTileShader.js';

export { buildTileBaseColors, tileBaseColor, varyTileColor } from './tilePaint.js';
export { bakedShade, makeTileGeometry, TILE_COLUMN_DEPTH } from './tileGeometry.js';

// Module-level singletons mutated in place during instance updates. Hoisting
// avoids per-render allocation on camera moves and fog-of-war recomputes.
const _dummy = new THREE.Object3D();
const _color = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };
export function TerrainTiles({
  entries,
  biome,
  count,
  visibleCoords,
  exploredCoords,
  onPointerOver,
  onClick,
  biomeMap,
  baseColors,
  tileData,
}: TerrainTilesProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const isWaterBiome = biome === 'ocean' || biome === 'coast' || biome === 'river';

  // Stable uniform objects (the riverTime pattern): onBeforeCompile binds
  // them once per program; the effect below swaps the texture when the
  // board's signature changes. Null → three binds its empty texture →
  // zero flags, never a missing-uniform error.
  const tileDataUniform = useRef<{ value: THREE.Texture | null }>({ value: null });
  const tileDataSize = useRef({ value: new THREE.Vector2(1, 1) });
  useEffect(() => {
    tileDataUniform.current.value = tileData?.texture ?? null;
    if (tileData) tileDataSize.current.value.set(tileData.width, tileData.height);
  }, [tileData]);

  // Each tile's plateau height: the outlines trace it and props, houses
  // and units build on it (tileSurfaceY + reliefAt).
  const elevations = useMemo(() => {
    return entries.map(({ tile }) => {
      const props = tile.props as Record<string, unknown> | undefined;
      return tileSurfaceY((props?.q as number) ?? 0, (props?.r as number) ?? 0, biomeMap);
    });
  }, [entries, biomeMap]);

  // One shared prism per biome; per-tile height comes from the instance
  // matrix. Water biomes stay flat (TerrainWater draws them).
  const geometry = useMemo(() => {
    if (isWaterBiome) {
      const g = new THREE.ShapeGeometry(hexShape);
      g.rotateX(-Math.PI / 2);
      return g;
    }
    return makeTileGeometry(hexNoise(0, 0), biome);
  }, [biome, isWaterBiome]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  // Material with snow cap vertex shader for mountains.
  // Switched from MeshStandardMaterial to MeshBasicMaterial (#131, audit
  // 2026-07-17): PBR shading was rendering 38% of map pixels as pure black
  // because instanceColor (forest palette '#3f8d4d' = rgb(63,141,77))
  // multiplied into the texture's diffuseColor, then dim cosmetic
  // ambient + directional crushed the result. MeshBasicMaterial skips
  // lighting — consistent with the scatter-prop fix from commit 2a1c7e9
  // (#122). The 3D read comes from the baked vertex-colour shading instead.
  const material = useMemo(() => {
    const mat = new THREE.MeshBasicMaterial({
      transparent: false,
      // `rgb()` string constructor (not setHex / setRGB with Linear color
      // space) survives the EffectComposer post-processing pipeline for
      // mid-tone values; empirical test in commit 2a1c7e9 (#122).
      color: 'rgb(255,255,255)',
      // Baked sun shading from makeTileGeometry (walls darker than tops).
      vertexColors: !isWaterBiome,
      fog: true,
    });

    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uRiverTime = riverTime;
      shader.uniforms.uTileData = tileDataUniform.current;
      shader.uniforms.uTileDataSize = tileDataSize.current;
      Object.assign(shader.uniforms, cloudShadowUniforms);
      // Per-tile crag: hash the instance seed per corner and displace the
      // raised rings (centre a little, peak fully, mid half). This is what
      // makes every mountain its own shape instead of a stamped tent. Land
      // only — water tiles are a flat shared hex with no ring attributes.
      let cragBlock = '';
      let cragPars = '';
      if (!isWaterBiome) {
        const [cragJag, cragPush] = CRAG_AMP[biome] ?? FLAT_CRAG;
        cragPars = 'attribute vec2 aRingCorner;\nattribute vec2 aSeed;\n';
        cragBlock = `
        {
          float _aRing = aRingCorner.x;
          float _aCorner = aRingCorner.y;
          float _jag = fract(sin(aSeed.x * 12.9 + (_aCorner + 1.0) * 78.233) * 43758.5453);
          float _push = fract(sin(aSeed.y * 26.7 + (_aCorner + 1.0) * 39.425) * 24634.6345);
          float _w = _aRing < 0.5 ? 0.35 : (_aRing < 1.5 ? 1.0 : (_aRing < 2.5 ? 0.5 : 0.0));
          transformed.y += (_jag - 0.35) * ${glslFloat(cragJag)} * _w;
          float _r = length(transformed.xz);
          if (_r > 0.01 && _w > 0.0) {
            vec2 _dir = transformed.xz / _r;
            transformed.xz += _dir * (_push - 0.5) * ${glslFloat(cragPush)} * _w;
          }
        }`;
      }
      // Inject world-space position varying (needed by both snow caps
      // and world-noise color variation below). Declared at top-level
      // (GLSL ES 3.00 forbids `out` inside function bodies). The world
      // position is captured AFTER the crag displacement so snow caps and
      // strata follow the deformed surface.
      shader.vertexShader = `${cragPars}
        varying vec3 _vWorldPos;
        varying float _vWorldY;
        ${shader.vertexShader}`.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>${cragBlock}
        vec4 _worldPos = instanceMatrix * vec4(transformed, 1.0);
        _vWorldPos = _worldPos.xyz;
        _vWorldY = _worldPos.y;`,
      );
      shader.fragmentShader = `
        varying vec3 _vWorldPos;
        varying float _vWorldY;
        ${shader.fragmentShader}`;

      // Inject snow caps: blend to white with height. The line starts just
      // above regular mountain peaks (~0.9), so only massif cores boosted by
      // mountainClusterBoost cross into full snow.
      if (biome === 'mountain' || biome === 'hills') {
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <color_fragment>',
          `#include <color_fragment>
          ${VOLCANO_SNOW_GLSL}
          float _snow = smoothstep(0.85, 1.5, _vWorldY) * _snowK;
          diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.92, 0.93, 0.97), _snow * 0.85);
          ${VOLCANO_TINT_GLSL}`,
        );
      }

      // Rocky strata + dirt + cracks: mountains get horizontal banding that
      // follows the deformed surface, low-frequency dirt patches so the grey
      // reads as soil-streaked rock, and thin crack veins of varying width.
      // All fade out toward the snow caps.
      if (biome === 'mountain') {
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <color_fragment>',
          `#include <color_fragment>
          {
            float _strata = sin(_vWorldY * 11.0 + _vWorldPos.x * 2.1 + _vWorldPos.z * 1.3) * 0.5 + 0.5;
            diffuseColor.rgb *= 0.88 + 0.18 * _strata;
            // Dirt: two overlapping low-frequency fields so patches vary.
            float _d1 = sin(_vWorldPos.x * 0.9 + sin(_vWorldPos.z * 0.7) * 1.4) * 0.5 + 0.5;
            float _d2 = sin(_vWorldPos.z * 0.6 - sin(_vWorldPos.x * 0.5 + 2.0) * 1.1) * 0.5 + 0.5;
            float _dirt = smoothstep(0.36, 0.72, _d1 * _d2);
            float _lum = dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114));
            vec3 _dirtCol = vec3(0.13, 0.095, 0.065) + _lum * vec3(0.36, 0.26, 0.16);
            diffuseColor.rgb = mix(diffuseColor.rgb, _dirtCol, _dirt * 0.62);
            // Cracks: two vein fields at different scales/frequencies.
            float _v1 = sin(_vWorldPos.x * 5.3 + sin(_vWorldPos.z * 3.7) * 1.7)
                      * sin(_vWorldPos.z * 4.2 - sin(_vWorldPos.x * 2.6) * 1.3);
            float _v2 = sin(_vWorldPos.x * 9.8 - sin(_vWorldPos.z * 7.1 + 1.0) * 2.2)
                      * sin(_vWorldPos.z * 8.3 + sin(_vWorldPos.x * 5.2) * 1.9);
            float _cracks = max(1.0 - smoothstep(0.0, 0.09, abs(_v1)), (1.0 - smoothstep(0.0, 0.05, abs(_v2))) * 0.6);
            diffuseColor.rgb *= 1.0 - _cracks * 0.38 * (1.0 - smoothstep(0.55, 0.8, _vWorldY));
          }`,
        );
      }

      // World-space noise to break up uniform tile colors.
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        float _bn = fract(sin(dot(_vWorldPos.xz * 0.45, vec2(127.1, 311.7))) * 43758.5453);
        _bn += fract(sin(dot(_vWorldPos.xz * 0.9 + 2.0, vec2(269.5, 183.3))) * 23421.6312);
        _bn = _bn * 0.5;
        diffuseColor.rgb *= 0.93 + 0.14 * _bn;
        ${CLOUD_SHADOW_GLSL_MAIN}`,
      );
      // Moving cloud shadows (AtmosphericClouds drives the uniforms; strength 0 = off).
      shader.fragmentShader = `${CLOUD_SHADOW_GLSL_PARS}\n${shader.fragmentShader}`;

      // Per-edge coasts and biome blends (terrainEdges.ts). Injected last so
      // it runs first, right after <color_fragment>.
      if (!isWaterBiome) {
        const edge = tileEdgeShader(biome);
        shader.vertexShader = `${edge.vertexPars}\n${shader.vertexShader}`.replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>\n${edge.vertexMain}`,
        );
        shader.fragmentShader = `${edge.fragmentPars}\n${shader.fragmentShader}`.replace(
          '#include <color_fragment>',
          `#include <color_fragment>\n${edge.fragmentMain}`,
        );
      }
    };
    // onBeforeCompile's source is the same for every biome, which is three's
    // default cache key, so without this one biome's program (snow caps, shore
    // colour) would be reused for all of them.
    mat.customProgramCacheKey = () => `terrain-tile:${biome}`;

    return mat;
  }, [biome, isWaterBiome]);

  // Update instance matrices -- each hex at its own height.
  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;

    const dummy = _dummy;
    const color = _color;
    const hsl = _hsl;

    entries.forEach(({ tile }, localIdx) => {
      const [x, , z] = tile.transform.position;
      const h = elevations[localIdx] ?? 0;
      dummy.position.set(x, h, z);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      mesh.setMatrixAt(localIdx, dummy.matrix);

      const props = tile.props as Record<string, unknown> | undefined;
      tileBaseColor(tile, color);

      // Fog of war — three-state: visible, explored-not-visible, unexplored.
      if (visibleCoords) {
        const q = props?.q as number | undefined;
        const r = props?.r as number | undefined;
        if (q !== undefined && r !== undefined) {
          const key = `${q},${r}`;
          if (!visibleCoords.has(key)) {
            color.getHSL(hsl);
            if (exploredCoords?.has(key)) {
              // Explored but not currently visible — dimmed terrain.
              color.setHSL(hsl.h, hsl.s * 0.4, hsl.l * 0.55);
            } else {
              // Never explored — very dark.
              color.setHSL(hsl.h, hsl.s * 0.15, hsl.l * 0.25);
            }
          }
        }
      }

      mesh.setColorAt(localIdx, color);
    });

    // Neighbour masks + colours for the coast and blend shading.
    const coords = entries.map(({ tile }) => {
      const props = tile.props as Record<string, unknown> | undefined;
      return [(props?.q as number) ?? 0, (props?.r as number) ?? 0] as const;
    });
    const attrs = buildTileEdgeAttributes(
      coords,
      biomeMap,
      baseColors ?? new Map(),
      visibleCoords,
      exploredCoords,
    );
    // aTone.z = this instance's GLOBAL tile index — the lookup key into the
    // flag texture (the flags moved out of the attribute, P7.13).
    for (let t = 0; t < entries.length; t++) attrs.tone[t * 3 + 2] = entries[t]!.idx;
    setInstancedAttribute(geometry, 'aEdgeMask', attrs.masks, 4);
    setInstancedAttribute(geometry, 'aNbA', attrs.nbA, 3);
    setInstancedAttribute(geometry, 'aNbB', attrs.nbB, 3);
    setInstancedAttribute(geometry, 'aTone', attrs.tone, 3);
    // Per-tile crag seed: feeds the vertex-shader displacement that breaks
    // the shared geometry's symmetry (aSeed is not set on water geometries —
    // their shader has no displacement block).
    if (!isWaterBiome) {
      const seeds = new Float32Array(coords.length * 2);
      coords.forEach(([q, r], t) => {
        seeds[t * 2] = hexNoise(q + 0.31, r - 0.77);
        seeds[t * 2 + 1] = hexNoise(q * 1.7 + 3.1, r * 2.3 + 1.7);
      });
      setInstancedAttribute(geometry, 'aSeed', seeds, 2);
    }

    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.count = count;
    // InstancedMesh caches its bounding sphere on the first frustum test. If
    // that frame ran before this effect, the sphere covers only identity
    // matrices (one hex at the origin) and the whole biome gets culled.
    mesh.computeBoundingSphere();
  }, [entries, count, elevations, visibleCoords, exploredCoords, geometry, biomeMap, baseColors]);

  return (
    <instancedMesh
      name={`terrain-${biome}`}
      ref={meshRef}
      args={[geometry, material, count]}
      receiveShadow
      castShadow={biome === 'mountain' || biome === 'hills'}
      renderOrder={RenderLayer.Terrain}
      frustumCulled
      onPointerOver={(e) => {
        e.stopPropagation();
        const i = e.instanceId;
        if (i !== undefined && onPointerOver) onPointerOver(entries[i]?.tile ?? null);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        if (onPointerOver) onPointerOver(null);
      }}
      onClick={(e) => {
        e.stopPropagation();
        const i = e.instanceId;
        if (i !== undefined && onClick) {
          const tile = entries[i]?.tile;
          if (tile) onClick(tile);
        }
      }}
    />
  );
}

/** Advances the shared edge-river flow uniform. Must be rendered in the scene. */

/**
 * The river's motion read as a rushing torrent at wall-clock rate — the
 * flow glint and meander wobble both ride `uRiverTime`, so this scales
 * the whole animation (currently 5× slower).
 */
const RIVER_TIME_SCALE = 0.2;

export function RiverAnimator() {
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      riverTime.value += (1 / 60) * RIVER_TIME_SCALE;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return null;
}
